import angular from "angular";

import { Dates, Taxcard, TaxCard2019Logic, TaxCardIncomeType, TaxcardKind, Translations } from "@salaxy/core";

import { SessionService } from "../../services";

/**
 * UI logic for adding new manual taxcards.
 * Designed to be used in a modal dialog: You must pass personal ID for the Worker in EditDialogParameters.
 */
export class TaxcardManualEditController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["SessionService"];

  /** The current taxcard that is being edited. */
  public current: Taxcard;

  /** Edit taxcard form: Used for checking the validity. */
  public editForm: angular.IFormController;

  /**
   * Today is explicitly defined, so that it can be changed in testing.
   */
  public today = Dates.getToday();

  /** If true, shows the today edit box (in test only) */
  public showToday = false;

  /** Settings / instructions for the "Create new" UI (show/hide inputs, formatting etc). */
  public addNewUi = {
    /** If true, shown the 1st tax percent */
    showTaxPercent: false,
    /** If true, shown the income limit AND 2nd tax pecent. */
    showIncomeLimit: false,
    /** If true, the require the income limit AND 2nd tax percent (default yearly). */
    requireIncomeLimit: false,
    /** If true, shows input for previous salaries paid. */
    showPreviousSalariesPaid: false,
    /** If true, the tax percents etc. cannot be edited (no taxcard household or 60%).*/
    readonly: false,
    /** Options for the start date: currently min and max dates. */
    startDateOptions: null as null | {
      minDate: string,
      maxDate: string,
    },
    /**
     * Years that are allowed in the year selection:
     * Current year + next year in December, previous year in January.
     */
    yearsAllowed: null as null | number[],
  };

  constructor(
    private sessionService: SessionService,
  ) {
    // Dependency injection
  }

  /**
   * Implement IController
   */
  public $onInit() {
    if (this.sessionService.isInRole("company")) {
      delete this.taxcardKindOptions.noWithholdingHousehold;
    }
  }

  /** Returns true if one of the edit forms is shown ad if it is valid. */
  public get isFormValid() {
    return this.editForm?.$valid && (this.current?.card?.kind || "undefined") != "undefined";
  }

  /**
   * Gets a row for data binding of external salaries paid outside Salaxy system.
   * Will create the row if it does not exist. The save method will remove any empty rows.
   */
  public get previousSalariesRow() {
    if (!this.current) {
      return null;
    }
    if (!this.current.incomeLog) {
      this.current.incomeLog = [];
    }
    if (!this.current.incomeLog.find((x) => x.type === TaxCardIncomeType.ExternalSalaries)) {
      this.current.incomeLog.push({
        type: TaxCardIncomeType.ExternalSalaries,
        id: null,
        description: Translations.get("SALAXY.NG1.CurrentTaxCardComponent.previousSalariesRow"),
        startDate: this.today,
        endDate: this.today,
        income: null,
        tax: null,
      });
    }
    return this.current.incomeLog.find((x) => x.type === TaxCardIncomeType.ExternalSalaries);
  }

  /** Taxcard kinds that should be allowed for the new manual taxcard. */
  public taxcardKindOptions = {
    "defaultYearly": "SALAXY.NG1.CurrentTaxCardComponent.taxcardKindOptions.defaultYearly",
    "replacement": "SALAXY.NG1.CurrentTaxCardComponent.taxcardKindOptions.replacement",
    "noTaxCard": "SALAXY.NG1.CurrentTaxCardComponent.taxcardKindOptions.noTaxCard",
    // This is removed from company role in init (only for household employers).
    "noWithholdingHousehold": "SALAXY.NG1.CurrentTaxCardComponent.taxcardKindOptions.noWithholdingHousehold",
  };

  /**
   * Resets the current taxcard as new (when the kind changes).
   * @param kind If specified, reset the taxcard to the given type.
   */
  public resetAsNew(kind?: TaxcardKind) {
    this.setCurrent(TaxCard2019Logic.getBlank(this.current.card.personalId, kind, null, this.today));
    this.updateUISettings();
  }

  /** Handles change of the year in the years dropdown. */
  public yearChanged() {
    this.setCurrent(TaxCard2019Logic.getBlank(this.current.card.personalId, this.current.card.kind, this.current.card.forYear, this.today));
    this.updateUISettings();
  }

  /** Sets the current item value keeping the original reference. */
  private setCurrent(newItem: Taxcard) {
    angular.copy(newItem, this.current);
    this.updateUISettings();
  }

  /** Defines which fields to show/hide, require etc. in the view */
  private updateUISettings() {
    const settings = {
      showTaxPercent: false,
      showIncomeLimit: false,
      requireIncomeLimit: false,
      showPreviousSalariesPaid: false,
      readonly: false,
      startDateOptions: null,
      yearsAllowed: null,
    };
    if (!this.current) {
      this.addNewUi = settings;
      return;
    }
    const todayYear = Dates.getYear(this.today);
    const month = Dates.getMonth(this.today);
    let yearsAllowed: number[];
    if (month === 1) {
      yearsAllowed = [todayYear, todayYear - 1];
    } else if (month === 12) {
      yearsAllowed = [todayYear, todayYear + 1];
    } else {
      yearsAllowed = [todayYear];
    }
    if (yearsAllowed.indexOf(this.current.card.forYear) < 0) {
      if (!this.current.id) {
        this.current.card.forYear = todayYear;
      }
    }
    const forYear = this.current.card.forYear;
    yearsAllowed = yearsAllowed.length < 2 ? null : yearsAllowed; // In the UI, the selection is not shown if there is only one item to select.
    settings.showTaxPercent = true;

    switch (this.current.card.kind) {
      case TaxcardKind.Auto:
        settings.showTaxPercent = false;
        break;
      case TaxcardKind.NoTaxCard:
      case TaxcardKind.NoWithholdingHousehold:
        settings.readonly = true;
        break;
      case TaxcardKind.Undefined:
        settings.readonly = true;
        settings.showTaxPercent = false;
        break;
      case TaxcardKind.DefaultYearly:
        settings.showIncomeLimit = true;
        settings.showPreviousSalariesPaid = true;
        settings.requireIncomeLimit = true;
        settings.yearsAllowed = yearsAllowed;
        break;
      case TaxcardKind.Replacement:
        settings.showIncomeLimit = true;
        settings.showPreviousSalariesPaid = true;
        settings.yearsAllowed = yearsAllowed;
        settings.startDateOptions = {
            minDate: Dates.asJSDate(forYear + "-01-01"),
            maxDate: Dates.asJSDate(forYear + "-11-31"),
          };
        break;
    }
    this.addNewUi = settings;
  }
}
